import { FC } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Button,
  Chip,
  ChipProps,
  Cluster,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
} from '@forto-dev/phoenix-ui-core/common'

import { copyTextToClipboard } from '@/utils/clipboard'
import { useLocalisation } from '@/utils/localisation'

import { tokenLogQueryOptions } from '../api/tokens.service'

const getChipVariantForResponseStatus = (responseStatus: number): ChipProps['variant'] => {
  if (responseStatus >= 200 && responseStatus < 300) {
    return 'positive'
  }

  if (responseStatus >= 300 && responseStatus < 400) {
    return 'warning'
  }

  if (responseStatus >= 400) {
    return 'negative'
  }

  return 'neutral'
}

export type TokenLogsDialogProps = {
  tokenId: string
  onClose?: () => void
}

export const TokenLogsDialog: FC<TokenLogsDialogProps> = ({ tokenId, onClose }) => {
  const { translateDate, translate } = useLocalisation()
  const { data: logs } = useSuspenseQuery(tokenLogQueryOptions(tokenId))

  const handleCopy = async (text: string) => {
    await copyTextToClipboard(text)
  }

  return (
    <Dialog
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose?.()
        }
      }}
    >
      <DialogContent maxWidth="full" maxHeight="full">
        <DialogHeader>
          <DialogTitle>{translate('integrations.tokens.logs.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {logs.length > 0 ? (
            <Stack>
              <Accordion type="multiple">
                {logs.map((log) => (
                  <AccordionItem key={log.id} value={log.id}>
                    <AccordionTrigger>
                      <Stack gap={4} x="start">
                        <Cluster gap={4} y="center">
                          <Chip variant={getChipVariantForResponseStatus(log.responseStatus)}>
                            {log.responseStatus}
                          </Chip>
                          <Typography variant="small-bold">{log.method.toUpperCase()}</Typography>
                          <Typography variant="small">{log.path}</Typography>
                        </Cluster>
                        <Typography color="muted" variant="small">
                          {translateDate('integrations_log_date', new Date(log.createdAt))}
                        </Typography>
                      </Stack>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Cluster gap={16}>
                        <Stack gap={16}>
                          <Typography variant="h6">{translate('integrations.tokens.logs.request.title')}</Typography>
                          {log.query && (
                            <>
                              <Cluster gap={8} y="center">
                                <Typography variant="small-bold">
                                  {translate('integrations.tokens.logs.request.query.title')}
                                </Typography>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <IconButton
                                      label={translate('actions.copy')}
                                      icon="FileCopy"
                                      onClick={() => handleCopy(JSON.stringify(log.query, null, '\t'))}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>{translate('integrations.tokens.logs.copyTooltip')}</TooltipContent>
                                </Tooltip>
                              </Cluster>
                              <code>{JSON.stringify(log.query, null, '\t')}</code>
                            </>
                          )}
                          {log.body && (
                            <>
                              <Cluster gap={8} y="center">
                                <Typography variant="small-bold">
                                  {translate('integrations.tokens.logs.request.body.title')}
                                </Typography>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <IconButton
                                      label={translate('actions.copy')}
                                      icon="FileCopy"
                                      onClick={() => handleCopy(JSON.stringify(log.body, null, '\t'))}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>{translate('integrations.tokens.logs.copyTooltip')}</TooltipContent>
                                </Tooltip>
                              </Cluster>
                              <code>{JSON.stringify(log.body, null, '\t')}</code>
                            </>
                          )}
                        </Stack>
                        <Stack gap={16}>
                          <Typography variant="h6">
                            {translate('integrations.tokens.logs.response.body.title')}
                          </Typography>
                          <Cluster gap={8} y="center">
                            <Typography variant="small-bold">
                              {translate('integrations.tokens.logs.response.body.title')}
                            </Typography>
                            <Tooltip>
                              <TooltipTrigger>
                                <IconButton
                                  label={translate('actions.copy')}
                                  icon="FileCopy"
                                  onClick={() => handleCopy(JSON.stringify(log.response, null, '\t'))}
                                />
                              </TooltipTrigger>
                              <TooltipContent>{translate('integrations.tokens.logs.copyTooltip')}</TooltipContent>
                            </Tooltip>
                          </Cluster>
                          <code>{JSON.stringify(log.response, null, '\t')}</code>
                        </Stack>
                      </Cluster>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Stack>
          ) : (
            <Alert title="No logs available yet." />
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline-primary"
            onClick={() => {
              onClose?.()
            }}
          >
            {translate('actions.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
